import React, { useEffect, useState } from 'react';
import Card from '../../components/card/Card';
import DropDown from '../../components/dropdown/DropDown';
import Input from '../../components/input/Input';
import Button from '../../components/button/Button';
import { useToast } from '../../components/toast/toast';
import {
  getReinsuranceInstitutions,
  saveInstrumentReinsurance,
  toInstrumentReinsurancePath,
} from '../../services/GuaranteesService';

type GuaranteeType = 'loan' | 'check' | 'promissory_note' | 'commercial_endorsement';

interface ReinsuranceFrameProps {
  guaranteeType: GuaranteeType;
  idGlobal: string;
  initialReinsured: boolean | null;
  initialInstitutionId: number | null;
  initialInstitutionName: string | null;
  initialPercentage: string | number | null;
  initialReinsuredEntityPays: boolean | null;
  /** Bottom margin of the row; pass '0' when the frame is already inside a parent grid row that owns its own spacing. */
  marginBottom?: string;
}

interface Institution {
  id: number;
  name: string;
}

const SUFFIX_BY_GUARANTEE_TYPE: Record<GuaranteeType, string> = {
  loan: 'loan',
  check: 'check',
  promissory_note: 'promissory',
  commercial_endorsement: 'commercial',
};

/** Converts string/boolean/null API values to a strict boolean or null. */
const parseBooleanValue = (value: unknown): boolean | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    if (value.toLowerCase() === 'true') return true;
    if (value.toLowerCase() === 'false') return false;
  }
  return null;
};

const toBooleanTitle = (value: boolean | null): string => (value === null ? 'No' : value ? 'Sí' : 'No');

const toOptionalBooleanTitle = (value: boolean | null): string =>
  value === null ? 'Seleccionar' : value ? 'Sí' : 'No';

const parseBooleanTitle = (title: string): boolean | null => {
  if (title === 'Sí') return true;
  if (title === 'No') return false;
  return null;
};

const FORM_CONTROL_GRID_ROW: React.CSSProperties = {
  display: 'grid',
  gap: '16px',
  alignItems: 'end',
};

const ReinsuranceFrame: React.FC<ReinsuranceFrameProps> = ({
  guaranteeType,
  idGlobal,
  initialReinsured,
  initialInstitutionId,
  initialInstitutionName,
  initialPercentage,
  initialReinsuredEntityPays,
  marginBottom = '32px',
}) => {
  const suffix = SUFFIX_BY_GUARANTEE_TYPE[guaranteeType];
  const [institutions, setInstitutions] = useState<Institution[]>([]);
  const [reinsured, setReinsured] = useState<boolean | null>(parseBooleanValue(initialReinsured));
  const [institutionId, setInstitutionId] = useState<number | null>(initialInstitutionId);
  const [institutionName, setInstitutionName] = useState<string | null>(initialInstitutionName);
  const [percentage, setPercentage] = useState<string>(
    initialPercentage === null || initialPercentage === undefined ? '' : String(initialPercentage),
  );
  const [reinsuredEntityPays, setReinsuredEntityPays] = useState<boolean | null>(
    parseBooleanValue(initialReinsuredEntityPays),
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchInstitutions = async () => {
      try {
        const response = await getReinsuranceInstitutions();
        if (response.success && 'data' in response && response.data) {
          setInstitutions(Array.isArray(response.data) ? response.data : []);
        }
      } catch (error) {
        console.error('Error fetching reinsurance institutions:', error);
      }
    };

    fetchInstitutions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedInstitutionName =
    (institutionId != null ? institutions.find((inst) => inst.id === institutionId)?.name : null) ??
    institutionName ??
    'Seleccionar';

  const percentageNumber = percentage === '' ? NaN : Number(percentage);
  const canSave =
    reinsured === false ||
    (institutionId != null && percentage !== '' && percentageNumber >= 0 && percentageNumber <= 100);

  const handleReinsuredChange = (title: string) => {
    setReinsured(parseBooleanTitle(title));
  };

  const handleInstitutionChange = (title: string) => {
    const institution = institutions.find((inst) => inst.name === title);
    if (institution) {
      setInstitutionId(institution.id);
      setInstitutionName(institution.name);
    }
  };

  const handlePercentageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPercentage(event.target.value);
  };

  const handleReinsuredEntityPaysChange = (title: string) => {
    setReinsuredEntityPays(parseBooleanTitle(title));
  };

  const handleSave = async () => {
    if (saving || !canSave) return;
    setSaving(true);
    try {
      const instrument = toInstrumentReinsurancePath(guaranteeType);
      const response = await saveInstrumentReinsurance(instrument, idGlobal, {
        reinsured: reinsured === true,
        reinsurance_institution_id: reinsured === false ? null : institutionId,
        percentage: reinsured === false ? null : percentageNumber,
        reinsured_entity_pays: reinsuredEntityPays,
      });

      const savedData =
        response && response.success && 'data' in response ? (response as any).data : null;

      if (!response || !response.success || !savedData) {
        useToast.error({
          title:
            (response as any)?.message || 'No se pudieron guardar los datos de reafianzamiento',
          id: 'reinsurance-toast',
        });
        return;
      }

      setReinsured(parseBooleanValue(savedData.reinsured));
      setInstitutionId(savedData.reinsurance_institution_id ?? null);
      setInstitutionName(savedData.reinsurance_institution_name ?? null);
      setPercentage(
        savedData.percentage === null || savedData.percentage === undefined
          ? ''
          : String(savedData.percentage),
      );
      setReinsuredEntityPays(parseBooleanValue(savedData.reinsured_entity_pays));

      useToast.success({
        title: 'Datos de reafianzamiento guardados',
        id: 'reinsurance-toast',
      });
    } catch (error: any) {
      const message =
        error?.response?.data?.message || 'No se pudieron guardar los datos de reafianzamiento';
      useToast.error({
        title: message,
        id: 'reinsurance-toast',
      });
    } finally {
      setSaving(false);
    }
  };

  const saveButton = (
    <Button
      id={`save-reinsurance-${suffix}`}
      testId={`save-reinsurance-${suffix}`}
      size="sm"
      onClick={handleSave}
      disabled={saving || !canSave}
    >
      Guardar
    </Button>
  );

  const reinsuredField = (
    <Card>
      <DropDown
        id={`reinsured-${suffix}`}
        label="Reafianzada"
        title={toBooleanTitle(reinsured)}
        placeholderTitle="Seleccionar"
        items={[{ label: 'Sí' }, { label: 'No' }]}
        onChange={handleReinsuredChange}
        size="md"
        marginTop="4px"
      />
    </Card>
  );

  const institutionField = (
    <Card>
      <DropDown
        id={`reinsurance-institution-${suffix}`}
        label="Entidad reafianzadora"
        title={selectedInstitutionName}
        placeholderTitle="Seleccionar"
        items={institutions.map((inst) => ({ label: inst.name }))}
        onChange={handleInstitutionChange}
        size="md"
        marginTop="4px"
        disabled={saving}
      />
    </Card>
  );

  const percentageField = (
    <Card>
      <Input
        id={`reinsurance-percentage-${suffix}`}
        label="Porcentaje"
        type="number"
        size="md"
        value={percentage}
        onChange={handlePercentageChange}
        placeholder="0-100"
        disabled={saving}
      />
    </Card>
  );

  const paysField = (
    <Card>
      <DropDown
        id={`reinsured-entity-pays-${suffix}`}
        label="Paga entidad reafianzadora"
        title={toOptionalBooleanTitle(reinsuredEntityPays)}
        placeholderTitle="Seleccionar"
        items={[{ label: 'Sí' }, { label: 'No' }]}
        onChange={handleReinsuredEntityPaysChange}
        size="md"
        marginTop="4px"
        disabled={saving}
      />
    </Card>
  );

  return (
    <div
      style={{
        ...FORM_CONTROL_GRID_ROW,
        gridTemplateColumns: reinsured
          ? 'repeat(4, minmax(0, 1fr)) auto'
          : 'repeat(4, minmax(0, 1fr))',
        marginBottom,
      }}
    >
      {reinsuredField}
      {reinsured ? (
        <>
          {institutionField}
          {percentageField}
          {paysField}
        </>
      ) : null}
      <div style={{ justifySelf: 'start' }}>{saveButton}</div>
    </div>
  );
};

export default ReinsuranceFrame;
